import { Base } from '@studiometa/js-toolkit';
import { gsap } from 'gsap';

/**
 * @typedef {{
 *   $refs: {
 *     firstWrap: HTMLElement,
 *     secondWrap: HTMLElement,
 *     thirdWrap: HTMLElement,
 *     percentage: HTMLElement,
 *     numbers: HTMLElement,
 *   }
 * }} LoaderProps
 */

/**
 * Loader component — fake 3-step counter animation.
 * @augments {Base<LoaderProps>}
 */
export default class Loader extends Base {
  static config = {
    name: 'Loader',
    refs: ['firstWrap', 'logo', 'secondWrap', 'thirdWrap', 'percentage', 'numbers'],
  };

  /**
   *
   */
  mounted() {
    this.play();
  }

  /**
   * Run the 3-step loading animation.
   * @returns {gsap.core.Timeline}
   */
  play() {
    const { firstWrap, secondWrap, thirdWrap, percentage, logo } = this.$refs;

    this.tl = gsap.timeline({
      onUpdate: () => {
        if (this.tl.progress() > 0.1 && this.tl.progress() < 0.15) {
          gsap.to(logo, { y: 0, opacity: 1, duration: 3, ease: 'power4.out' });
        }
      },
    });

    gsap.defaults({
      ease: 'expo.inOut',
      duration: 1.2,
    });

    const rand1 = gsap.utils.random([2, 3, 4]);
    const rand2 = gsap.utils.random([5, 6]);
    const rand3 = gsap.utils.random([1, 5]);
    const rand4 = gsap.utils.random([7, 8, 9]);

    // Initial states
    this.tl.set(this.$refs.numbers, { visibility: 'visible' });
    this.tl.set([firstWrap, percentage], { yPercent: 100 });
    this.tl.set([secondWrap, thirdWrap], { yPercent: 10 });

    // Step 1
    this.tl.to(percentage, { yPercent: 0 });
    this.tl.to(secondWrap, { yPercent: (rand1 - 1) * -10 }, '<');
    this.tl.to(thirdWrap, { yPercent: (rand3 - 1) * -10 }, '<');

    // Step 2
    this.tl.to(secondWrap, { yPercent: (rand2 - 1) * -10 });
    this.tl.to(thirdWrap, { yPercent: (rand4 - 1) * -10 }, '<');

    // Step 3 — 100%
    this.tl.to(secondWrap, { yPercent: -90 });
    this.tl.to(thirdWrap, { yPercent: -90 }, '<');
    this.tl.to(firstWrap, { yPercent: 0 }, '<');

    // Exit — each wrap slides up out of its overflow-hidden parent
    this.tl.to(firstWrap, { yPercent: -100, duration: 0.8, ease: 'power3.inOut' });
    this.tl.to(secondWrap, { yPercent: -100, duration: 0.8, ease: 'power3.inOut' }, '<0.05');
    this.tl.to(thirdWrap, { yPercent: -100, duration: 0.8, ease: 'power3.inOut' }, '<0.05');
    this.tl.to(percentage, { yPercent: -100, duration: 0.8, ease: 'power3.inOut' }, '<0.05');

    // Dispatch loader:complete avant le fade pour enchaîner les intros
    this.tl.call(() => {
      window.__loaderComplete = true;
      document.dispatchEvent(new CustomEvent('loader:complete'));
    });

    // Fade out
    this.tl.to(
      this.$el,
      {
        opacity: 0,
        duration: 1,
        ease: 'power4.out',
      },
      '-=0.4',
    );

    return this.tl;
  }
}
